import React, { useState, useRef, useEffect } from "react";
import "./Tooltip.css";

export type TooltipPlacement = "top" | "bottom" | "left" | "right";

export interface TooltipProps {
  /**
   * Texto del tooltip
   */
  text: string;
  /**
   * Elemento que activa el tooltip
   */
  children: React.ReactNode;
  /**
   * Posición del tooltip
   */
  placement?: TooltipPlacement;
  /**
   * Clase CSS adicional
   */
  className?: string;
  /**
   * Si el tooltip está deshabilitado
   */
  disabled?: boolean;
}

export const Tooltip: React.FC<TooltipProps> = ({
  text,
  children,
  placement = "top",
  className = "",
  disabled = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isVisible) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isVisible]);

  const tooltipClasses = [
    "qida-tooltip",
    `qida-tooltip--${placement}`,
    isVisible ? "qida-tooltip--visible" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const handleMouseEnter = () => {
    if (!disabled) {
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (!disabled) {
      setIsVisible(false);
    }
  };

  const handleFocus = () => {
    if (!disabled) {
      setIsVisible(true);
    }
  };

  const handleBlur = () => {
    if (!disabled) {
      setIsVisible(false);
    }
  };

  return (
    <div
      className="qida-tooltip-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      ref={triggerRef}
      aria-describedby={isVisible ? "tooltip" : undefined}
    >
      {children}
      <div
        className={tooltipClasses}
        role="tooltip"
        id="tooltip"
        ref={tooltipRef}
      >
        <div className="qida-tooltip__content">
          <div className="qida-tooltip__text">{text}</div>
        </div>
        <div className="qida-tooltip__arrow" />
      </div>
    </div>
  );
};

export default Tooltip;
